import { MOODS, MAX_MOODS_PER_PALETTE } from '../utils/moods';

const MOOD_COLORS = {
  Greenish:  'bg-green-100 text-green-700 border-green-200',
  Yellowish: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  Reddish:   'bg-red-100 text-red-700 border-red-200',
  Blueish:   'bg-blue-100 text-blue-700 border-blue-200',
  Purplish:  'bg-purple-100 text-purple-700 border-purple-200',
  Warm:      'bg-orange-100 text-orange-700 border-orange-200',
  Pastel:    'bg-pink-100 text-pink-600 border-pink-200',
  Vivid:     'bg-fuchsia-100 text-fuchsia-700 border-fuchsia-200',
  Summer:    'bg-amber-100 text-amber-700 border-amber-200',
  Spring:    'bg-lime-100 text-lime-700 border-lime-200',
  Autumn:    'bg-orange-100 text-orange-800 border-orange-300',
  Winter:    'bg-sky-100 text-sky-700 border-sky-200',
  Moody:     'bg-slate-100 text-slate-600 border-slate-300',
  Neutral:   'bg-stone-100 text-stone-600 border-stone-200',
  Retro:     'bg-amber-100 text-amber-800 border-amber-300',
  Vintage:   'bg-rose-100 text-rose-700 border-rose-200',
  Neon:      'bg-cyan-100 text-cyan-700 border-cyan-200',
  Day:       'bg-yellow-50 text-yellow-600 border-yellow-200',
  Night:     'bg-indigo-100 text-indigo-700 border-indigo-200',
  Dark:      'bg-slate-200 text-slate-800 border-slate-400',
  Soft:      'bg-rose-50 text-rose-500 border-rose-200',
  Skin:      'bg-orange-50 text-orange-600 border-orange-200',
  Earth:     'bg-amber-100 text-amber-900 border-amber-300',
  Cottage:   'bg-rose-100 text-rose-800 border-rose-200',
};

const SELECTED_COLORS = {
  Greenish:  'bg-green-500 text-white border-green-500',
  Yellowish: 'bg-yellow-400 text-white border-yellow-400',
  Reddish:   'bg-red-500 text-white border-red-500',
  Blueish:   'bg-blue-500 text-white border-blue-500',
  Purplish:  'bg-purple-500 text-white border-purple-500',
  Warm:      'bg-orange-500 text-white border-orange-500',
  Pastel:    'bg-pink-400 text-white border-pink-400',
  Vivid:     'bg-fuchsia-500 text-white border-fuchsia-500',
  Summer:    'bg-amber-500 text-white border-amber-500',
  Spring:    'bg-lime-500 text-white border-lime-500',
  Autumn:    'bg-orange-600 text-white border-orange-600',
  Winter:    'bg-sky-500 text-white border-sky-500',
  Moody:     'bg-slate-500 text-white border-slate-500',
  Neutral:   'bg-stone-400 text-white border-stone-400',
  Retro:     'bg-amber-600 text-white border-amber-600',
  Vintage:   'bg-rose-500 text-white border-rose-500',
  Neon:      'bg-cyan-500 text-white border-cyan-500',
  Day:       'bg-yellow-500 text-white border-yellow-500',
  Night:     'bg-indigo-600 text-white border-indigo-600',
  Dark:      'bg-slate-700 text-white border-slate-700',
  Soft:      'bg-rose-400 text-white border-rose-400',
  Skin:      'bg-orange-400 text-white border-orange-400',
  Earth:     'bg-amber-700 text-white border-amber-700',
  Cottage:   'bg-rose-400 text-white border-rose-400',
};

/**
 * Compact mood chip strip — read-only display.
 * Used on palette cards to show assigned moods.
 */
export function MoodChips({ moodNames, className = '' }) {
  if (!moodNames || moodNames.length === 0) return null;
  return (
    <div className={`flex flex-wrap gap-1 ${className}`}>
      {moodNames.map(mood => (
        <span
          key={mood}
          className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full border ${MOOD_COLORS[mood] || 'bg-slate-100 text-slate-600 border-slate-200'}`}
        >
          {mood}
        </span>
      ))}
    </div>
  );
}

/**
 * Interactive mood picker.
 * @param {string[]} selected - array of selected mood names
 * @param {function} onChange - called with new array of mood names
 */
export default function MoodPicker({ selected = [], onChange }) {
  const atMax = selected.length >= MAX_MOODS_PER_PALETTE;

  const toggle = (mood) => {
    if (selected.includes(mood)) {
      onChange(selected.filter(m => m !== mood));
    } else if (!atMax) {
      onChange([...selected, mood]);
    }
  };

  return (
    <div>
      <div className="flex flex-wrap gap-1.5">
        {MOODS.map(mood => {
          const isSelected = selected.includes(mood);
          const disabled = !isSelected && atMax;
          return (
            <button
              key={mood}
              type="button"
              onClick={() => toggle(mood)}
              disabled={disabled}
              className={`text-xs font-medium px-2 py-1 rounded-full border transition-all ${
                isSelected
                  ? SELECTED_COLORS[mood] || 'bg-slate-700 text-white border-slate-700'
                  : disabled
                    ? 'bg-slate-50 text-slate-300 border-slate-200 cursor-not-allowed'
                    : `${MOOD_COLORS[mood] || 'bg-slate-100 text-slate-600 border-slate-200'} opacity-50 hover:opacity-80`
              }`}
            >
              {mood}
            </button>
          );
        })}
      </div>
      <p className="text-[10px] text-slate-400 mt-2">
        {selected.length}/{MAX_MOODS_PER_PALETTE} moods selected
      </p>
    </div>
  );
}

export { MOOD_COLORS, SELECTED_COLORS };
